/**
 * Login — split-screen entry. Animated ASCII math-field on the left, sign-in
 * form on the right. Auth is Google sign-in (same allowlist/verify flow as the
 * dashboard); once a token is verified the Console orchestrator swaps this
 * screen for the console. Wallet connection happens later, inside the console.
 */

import { useRef, useState } from "react";
import { GoogleSignInButton } from "@thema/ui";
import { useAuth } from "../contexts/AuthContext";
import { GOOGLE_CLIENT_ID } from "../auth-config";
import { AsciiField } from "./AsciiField";
import { Logo } from "./Brand";
import { Icons, GoogleG } from "./icons";
import { WaitlistDialog } from "./WaitlistDialog";

function TrustItem({ icon, label }: { icon: keyof typeof Icons; label: string }) {
  const Ico = Icons[icon];
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <Ico size={16} style={{ color: "var(--accent)" }} />
      <span className="mono" style={{ fontSize: 11.5, color: "var(--text-soft)", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>
        {label}
      </span>
    </div>
  );
}

function LoginArt() {
  return (
    <div style={{ position: "relative", height: "100%", overflow: "hidden", display: "flex", flexDirection: "column", justifyContent: "space-between", padding: "clamp(32px,4vw,56px)" }}>
      <div
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 0,
          background:
            "radial-gradient(55% 45% at 20% 10%, rgba(61,231,208,0.12), transparent 60%)," +
            "radial-gradient(50% 42% at 88% 16%, rgba(59,130,246,0.12), transparent 62%)," +
            "radial-gradient(55% 55% at 70% 92%, rgba(208,181,111,0.10), transparent 60%)," +
            "linear-gradient(180deg, #F4F8FC, #EAF0F7 60%, #F2F6FB)",
        }}
      />
      <div style={{ position: "absolute", inset: 0, zIndex: 1 }}>
        <AsciiField />
      </div>

      {/* top: brand + tagline */}
      <div style={{ position: "relative", zIndex: 2, pointerEvents: "none" }}>
        <div style={{ pointerEvents: "auto", display: "inline-block" }}>
          <Logo size={34} />
        </div>
        <div className="eyebrow rise d1" style={{ marginTop: "clamp(40px,8vh,90px)" }}>
          On-chain derivatives infrastructure
        </div>
        <h1 className="serif rise d2" style={{ fontSize: "clamp(38px,4vw,62px)", lineHeight: 1.04, fontWeight: 700, letterSpacing: "-0.02em", margin: "16px 0 0", maxWidth: 560 }}>
          Market-aware
          <br />
          crypto assets.
        </h1>
        <div className="rise d3" style={{ height: 4, width: 120, background: "linear-gradient(90deg, var(--accent), transparent)", borderRadius: 9, margin: "20px 0 22px", transformOrigin: "left", animation: "underlineSweep 0.9s var(--smooth) 0.5s both" }} />
        <p className="rise d4" style={{ fontSize: "clamp(15px,1.2vw,17px)", lineHeight: 1.6, color: "var(--text-soft)", maxWidth: 440, margin: 0 }}>
          Thema adds mechanistic value as yield to tokens and token treasuries.
        </p>
      </div>

      {/* floating online-OMTs chip */}
      <div className="glass pop d5" style={{ position: "absolute", zIndex: 2, right: "clamp(28px,4vw,56px)", top: "51%", borderRadius: 14, padding: "12px 16px", display: "flex", alignItems: "center", gap: 12, pointerEvents: "none" }}>
        <span className="dot" />
        <div>
          <div className="eyebrow" style={{ fontSize: 9.5 }}>Online MASTs</div>
          <div className="mono" style={{ fontSize: 15, fontWeight: 600, color: "var(--accent)" }}>TBtc · TMantaBnb</div>
        </div>
      </div>

      {/* bottom: trust row */}
      <div className="fade d6" style={{ position: "relative", zIndex: 2, display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap", pointerEvents: "none" }}>
        <TrustItem icon="shield" label="Audited contracts" />
        <TrustItem icon="globe" label="Live on BNB & Base" />
        <TrustItem icon="lock" label="Safe custody" />
      </div>
    </div>
  );
}

function LoginForm() {
  const { login, logout, isLoading } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [waitlistEmail, setWaitlistEmail] = useState<string | null>(null);
  const triggerRef = useRef<(() => void) | null>(null);

  const onSuccess = async (token: string) => {
    setError(null);
    const outcome = await login(token);
    // `allowed` → the Console orchestrator swaps this screen for the console.
    if (outcome.status === "waitlist") {
      setWaitlistEmail(outcome.email);
    } else if (outcome.status === "error") {
      setError(outcome.message);
    }
  };

  const trigger = () => triggerRef.current?.();

  // From the waitlist dialog: drop the denied identity and reopen Google so the
  // user can pick a different (allowlisted) account.
  const tryAnotherAccount = () => {
    setWaitlistEmail(null);
    setError(null);
    logout();
    setTimeout(() => triggerRef.current?.(), 0);
  };

  return (
    <>
    <div style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", padding: "40px clamp(24px,4vw,72px)" }}>
      {/* Hidden Google Identity button; our buttons drive it via triggerRef. */}
      <GoogleSignInButton onSuccess={onSuccess} onError={setError} clientId={GOOGLE_CLIENT_ID} triggerRef={triggerRef} />

      <div className="rise d2" style={{ width: "100%", maxWidth: 380 }}>
        <div className="login-mobile-logo" style={{ marginBottom: 28 }}>
          <Logo size={28} />
        </div>

        <div className="eyebrow" style={{ marginBottom: 12 }}>Welcome back</div>
        <h2 className="serif" style={{ fontSize: 30, fontWeight: 700, letterSpacing: "-0.01em", margin: 0 }}>Sign in to your console</h2>
        <p style={{ fontSize: 14.5, color: "var(--text-soft)", margin: "10px 0 30px" }}>Access analytics, vaults, and structured products.</p>

        {/* Google */}
        <button
          className="btn"
          style={{ width: "100%", height: 52, background: "var(--surface)", border: "1px solid var(--border-strong)", color: "var(--text)", fontSize: 14, textTransform: "none", letterSpacing: "0", fontFamily: "var(--sans)", fontWeight: 600, boxShadow: "var(--card-shadow)" }}
          disabled={isLoading}
          onClick={trigger}
        >
          {isLoading ? (
            <>
              <Icons.spark size={18} className="spin" /> Authenticating…
            </>
          ) : (
            <>
              <GoogleG size={19} /> Continue with Google
            </>
          )}
        </button>

        {error && (
          <p style={{ marginTop: 16, fontSize: 12.5, color: "var(--neg)", textAlign: "center", lineHeight: 1.5 }}>{error}</p>
        )}

        <p style={{ fontSize: 12, color: "var(--micro)", textAlign: "center", marginTop: 28, lineHeight: 1.6 }}>
          By continuing you agree to Thema's <span className="link">Terms</span> &amp; <span className="link">Privacy Policy</span>.
        </p>
      </div>
    </div>
    {waitlistEmail && (
      <WaitlistDialog
        email={waitlistEmail}
        onClose={() => setWaitlistEmail(null)}
        onTryAnother={tryAnotherAccount}
      />
    )}
    </>
  );
}

export default function Login() {
  return (
    <div className="login-split">
      <div className="login-art-pane">
        <LoginArt />
      </div>
      <div className="login-form-pane">
        <LoginForm />
      </div>
    </div>
  );
}
